// src/pages/QuestionnairePage/steps/Step4Retinol.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Sparkles, Plus, Check, Stethoscope, Calendar } from 'lucide-react';

// ✅ Только строгие значения 1-6 недель (без <1 и >6)
export type RetinolDurationValue = 1 | 2 | 3 | 4 | 5 | 6;

const StepCard = styled(motion.div)`
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-medium);
  border-radius: 24px;
  padding: 32px 20px;
  box-shadow:
    0 8px 32px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  @media (max-width: 375px) {
    padding: 28px 16px;
  }

  [data-theme="dark"] & {
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::before {
      background: linear-gradient(90deg, #9d8eee, #a0d8f0);
    }
  }
`;

const Question = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 24px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #7B68EE;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    svg {
      color: #C4B5FD;
    }
  }
`;

const YesNoGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const ChipButton = styled(motion.button).attrs({ type: 'button' })<{ $active: boolean }>`
  flex: 1;
  padding: 20px 24px !important;
  border-radius: 16px !important;
  border: 2px solid ${props => props.$active
    ? 'var(--accent-color, #7B68EE)'
    : 'var(--border-color, rgba(123, 104, 238, 0.2))'
  } !important;
  background: ${props => props.$active
    ? 'var(--gradient-accent, linear-gradient(135deg, #7B68EE, #9966CC))'
    : 'var(--surface-color, rgba(255, 255, 255, 0.92))'
  } !important;
  color: ${props => props.$active
    ? 'var(--text-on-accent, #FFFFFF) !important'
    : 'var(--text-primary, #000000) !important'
  };
  font-size: 16px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  box-shadow: ${props => props.$active
    ? 'var(--shadow-accent, 0 8px 22px rgba(123, 104, 238, 0.35))'
    : 'var(--shadow-sm, 0 4px 12px rgba(15, 23, 42, 0.08))'
  } !important;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  outline: none !important;

  svg {
    color: ${props => props.$active ? 'var(--text-on-accent, #FFFFFF) !important' : 'var(--accent-color, #7B68EE) !important'};
  }

  &:hover {
    transform: translateY(-2px);
    border-color: var(--accent-color, #7B68EE) !important;
    box-shadow: var(--shadow-accent-hover, 0 10px 28px rgba(123, 104, 238, 0.35)) !important;
  }

  &:active {
    transform: translateY(0);
  }

  /* ✅ Темная тема через CSS-переменные */
  [data-theme="dark"] & {
    --accent-color: #C4B5FD;
    --border-color: rgba(196, 181, 253, 0.3);
    --gradient-accent: linear-gradient(135deg, #9d8eee, #b896dc);
    --surface-color: rgba(255, 255, 255, 0.08);
    --text-primary: #FFFFFF;
    --text-on-accent: #FFFFFF;
    --shadow-accent: 0 8px 22px rgba(157, 142, 238, 0.4);
    --shadow-sm: 0 4px 12px rgba(0, 0, 0, 0.3);
    --shadow-accent-hover: 0 10px 28px rgba(157, 142, 238, 0.45);
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonLabel = styled.span`
  font-size: 15px;
`;

const InfoText = styled(motion.p)`
  font-size: 14px;
  color: var(--tg-theme-text-color, rgba(0,0,0,0.7));
  line-height: 1.5;
  margin: 16px 0 0 0;
  padding: 12px;
  background: rgba(123, 104, 238, 0.08);
  border-radius: 8px;
  border-left: 3px solid var(--accent-color, #7B68EE);

  [data-theme="dark"] & {
    --accent-color: #C4B5FD;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(196, 181, 253, 0.12);
    border-left-color: #C4B5FD;
  }
`;

const DurationSection = styled(motion.div)`
  margin-top: 8px;
`;

const DurationTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 8px 0;

  [data-theme="dark"] & {
    color: #FFFFFF;
  }
`;

const DurationSubtitle = styled.p`
  font-size: 13px;
  color: var(--tg-theme-hint-color, rgba(0,0,0,0.6));
  margin: 0 0 18px 0;
  line-height: 1.4;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }
`;

// ✅ Выравнивание слайдера с учётом бегунка
const SliderWrapper = styled.div`
  padding: 0 6px; /* выравнивает с учётом track */
`;

const DurationSlider = styled.input`
  width: 100%;
  appearance: none;
  height: 4px;
  background: linear-gradient(90deg, rgba(123, 104, 238, 0.2), rgba(123, 104, 238, 0.5));
  border-radius: 999px;
  outline: none;
  margin: 0;
  accent-color: #7B68EE;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 24px;
    height: 24px;
    background: #7B68EE;
    border-radius: 50%;
    border: 3px solid #FFFFFF;
    box-shadow: 0 4px 14px rgba(123, 104, 238, 0.35);
    cursor: pointer;
    transition: transform 0.2s ease;
  }

  &::-webkit-slider-thumb:active {
    transform: scale(1.05);
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    background: #7B68EE;
    border-radius: 50%;
    border: 3px solid #FFFFFF;
    box-shadow: 0 4px 14px rgba(123, 104, 238, 0.35);
    cursor: pointer;
  }

  &::-moz-range-track {
    height: 4px;
    background: transparent;
  }
`;

// ✅ Выравнивание меток с учётом половины размера бегунка
const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 12px; /* половина ширины бегунка (24px / 2) */
  margin-top: 10px;
  font-size: 11px;
  color: rgba(0,0,0,0.55);
  min-width: 14px; /* чтобы жирный шрифт не сдвигал */

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SliderLabel = styled.span<{ $active: boolean }>`
  font-weight: ${props => (props.$active ? 700 : 500)};
  color: ${props => (props.$active ? 'var(--accent-color, #7B68EE)' : 'var(--tg-theme-hint-color, rgba(0,0,0,0.55))')};
  transition: color 0.2s ease, font-weight 0.2s ease;

  [data-theme="dark"] & {
    --accent-color: #C4B5FD;
    color: ${props => (props.$active ? '#C4B5FD' : 'rgba(255, 255, 255, 0.6)')};
  }
`;

const DurationHint = styled.p`
  margin: 18px 0 0 0;
  font-size: 14px;
  color: var(--tg-theme-text-color, rgba(0,0,0,0.75));
  text-align: center;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const DurationHighlight = styled.strong`
  font-weight: 700;
  color: var(--accent-color, #7B68EE);

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

// ✅ СТРОГО 6 фиксированных значений (1-6 недель)
const DURATION_OPTIONS: Array<{
  value: RetinolDurationValue;
  label: string;
  description: string;
}> =[
  { value: 1, label: '1', description: '1 неделю' },
  { value: 2, label: '2', description: '2 недели' },
  { value: 3, label: '3', description: '3 недели' },
  { value: 4, label: '4', description: '4 недели' },
  { value: 5, label: '5', description: '5 недель' },
  { value: 6, label: '6', description: '6 недель' },
];

/**
 * ✅ Конвертирует RetinolDurationValue (1-6) в число недель для API
 * Теперь это прямое отображение без преобразования
 */
export function retinolDurationToWeeks(duration: RetinolDurationValue | null): number | null {
  // ✅ Прямое возвращение значения с дополнительной защитой
  if (!duration) return null;

  // ✅ Кламп на случай некорректных данных
  const weeks = Number(duration);
  return Math.max(1, Math.min(6, weeks));
}

const DiseaseAlert = styled(motion.div)`
  margin-top: 24px;
  padding: 24px;
  border-radius: 20px;
  background: linear-gradient(135deg, 
    rgba(239, 68, 68, 0.12),
    rgba(220, 38, 38, 0.08)
  );
  border: 2px solid rgba(239, 68, 68, 0.25);
  text-align: center;
`;

const AlertIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #EF4444;

  svg {
    color: #EF4444;
  }
`;

const AlertTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #EF4444;
  margin: 0 0 12px 0;
`;

const AlertText = styled.p`
  font-size: 15px;
  color: var(--tg-theme-text-color, rgba(0,0,0,0.75));
  line-height: 1.6;
  margin: 0 0 20px 0;
  text-align: center; // ✅ FIX: центрирование текста

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const ConsultButton = styled(motion.a)`
  display: flex; // ✅ FIX: было inline-flex, теперь flex для полной ширины
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%; // ✅ FIX: добавлена полная ширина
  padding: 14px 24px;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white !important;
  text-decoration: none;
  border-radius: 16px;
  font-weight: 600;
  font-size: 15px;
  box-shadow: 0 8px 24px rgba(123, 104, 238, 0.3);

  svg {
    color: white !important;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(123, 104, 238, 0.4);
  }
`;

interface Step4RetinolProps {
  hasDisease: boolean;
  usesRetinol: boolean | null;
  retinolDuration: RetinolDurationValue | null;
  onChange: (updates: Partial<{
    usesRetinol: boolean;
    retinolDuration: RetinolDurationValue | null;
  }>) => void;
}

export const Step4Retinol: React.FC<Step4RetinolProps> = ({
  hasDisease,
  usesRetinol,
  retinolDuration,
  onChange,
}) => {
  if (hasDisease) {
    return (
      <StepCard
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Question>
          <Check size={24} />
          Спасибо за ответы!
        </Question>

        <DiseaseAlert
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <AlertIcon>
            <Stethoscope size={48} />
          </AlertIcon>
          <AlertTitle>Требуется консультация</AlertTitle>
          <AlertText>
            Введение ретинола при кожных заболеваниях возможно только
            после консультации специалиста.
          </AlertText>
          <AlertText>
            Запишитесь на <strong>бесплатную консультацию</strong> к косметологу
            в наш Центр Диагностики Кожи.
          </AlertText>
          <ConsultButton
            href="https://geltek.ru/skin-diagnostic-center/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Calendar size={18} />
            Записаться на консультацию
          </ConsultButton>

          <AlertText style={{ marginTop: 20, fontSize: 13, fontStyle: 'italic' }}>
            💡 Кнопка «Готово» недоступна до получения разрешения специалиста
          </AlertText>
        </DiseaseAlert>
      </StepCard>
    );
  }

  // ✅ Прямое использование значения недель (1-6)
  const activeOption =
    DURATION_OPTIONS.find(option => option.value === retinolDuration) ?? DURATION_OPTIONS[0];
  const sliderValue = retinolDuration ?? 1;

  const handleRetinolToggle = (value: boolean) => {
    if (!value) {
      onChange({ usesRetinol: false, retinolDuration: null });
      return;
    }
    onChange({ usesRetinol: true, retinolDuration: retinolDuration ?? 1 });
  };

  // ✅ С жёстким клампом: ТОЛЬКО значения 1-6, независимо от скролла/свайпа
  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = Number(event.target.value);
    // ✅ Защита от любых манипуляций: кламп 1-6
    const clampedValue = Math.max(1, Math.min(6, rawValue)) as RetinolDurationValue;
    onChange({ retinolDuration: clampedValue });
  };

  return (
    <StepCard
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Question>
        <Sparkles size={24} />
        Уже используете ретинол?
      </Question>

      <YesNoGroup>
        <ChipButton
          $active={usesRetinol === false}
          onClick={() => handleRetinolToggle(false)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon>
            <Plus size={28} />
          </ButtonIcon>
          <ButtonLabel>Ещё нет</ButtonLabel>
        </ChipButton>

        <ChipButton
          $active={usesRetinol === true}
          onClick={() => handleRetinolToggle(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon>
            <Check size={28} />
          </ButtonIcon>
          <ButtonLabel>Да</ButtonLabel>
        </ChipButton>
      </YesNoGroup>

      {usesRetinol === true && (
        <DurationSection
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <DurationTitle>Как долго Вы применяете ретинол?</DurationTitle>
          <DurationSubtitle>
            Укажите приблизительный срок на шкале, чтобы подобрать комфортный режим.
          </DurationSubtitle>

          <SliderWrapper>
            {/* ✅ СТРОГО: min=1, max=6, step=1 */}
            <DurationSlider
              type="range"
              min={1}
              max={6}
              step={1}
              value={sliderValue}
              onChange={handleDurationChange}
            />
            <SliderLabels>
              {DURATION_OPTIONS.map((option) => (
                <SliderLabel
                  key={option.value}
                  $active={option.value === activeOption.value}
                >
                  {option.label}
                </SliderLabel>
              ))}
            </SliderLabels>
          </SliderWrapper>

          <DurationHint>
            Применяю ретинол{' '}
            <DurationHighlight>{activeOption.description}</DurationHighlight>
          </DurationHint>
        </DurationSection>
      )}

      {usesRetinol !== null && (
        <InfoText
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {usesRetinol
            ? '✨ Отлично! Мы учтём Ваш опыт использования ретинола при составлении плана.'
            : '🎉 Тогда начнём! Это правильный шаг к здоровой и красивой коже!'}
        </InfoText>
      )}
    </StepCard>
  );
};